import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useJobCenterOptional } from '../contexts/JobCenterContext';
import { jobWarnings } from '../api/jobsApi';

// Toast subjects in the same clinician vocabulary as the Job Center's action
// lines (JOB_ACTIONS) — a toast and the drawer must never name the same work
// differently.
const STATUS_LABELS: Record<string, string> = {
  'dt_core.build': 'Digital twin build',
  'dt_core.update': 'Digital twin update',
  'dt_core.remediate': 'Digital twin preparation',
  'dt_core.fleet_heal': 'Patient narrative refresh',
  'synthetic.test': 'System self-test',
};

function describe(jobType: string): string {
  return STATUS_LABELS[jobType] || jobType.split('.').pop() || jobType;
}

/**
 * Fires a completion/failure toast for jobs tracked by JobCenterContext.
 * Consumes the context's already-hydrated state rather than opening a second
 * SSE connection or poll -- this hook is purely a view over that state.
 */
export function useJobCenterToasts() {
  const jobCenter = useJobCenterOptional();
  const seenStatusRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!jobCenter) return;
    for (const job of jobCenter.jobs) {
      const prevStatus = seenStatusRef.current.get(job.job_id);
      seenStatusRef.current.set(job.job_id, job.status);
      if (prevStatus === undefined || prevStatus === job.status) continue;

      if (job.status === 'completed') {
        // A prepare is fail-open per step, so "completed" alone does not mean
        // "everything worked". Firing a success toast over a failed narrative
        // actively teaches the clinician to trust an incomplete twin.
        const warnings = jobWarnings(job);
        if (warnings.length > 0) {
          toast.warning(`${describe(job.job_type)} finished with gaps`, {
            description:
              warnings.length === 1
                ? '1 step needs review — open the Job Center for details'
                : `${warnings.length} steps need review — open the Job Center for details`,
          });
        } else {
          toast.success(`${describe(job.job_type)} completed`);
        }
      } else if (job.status === 'failed') {
        toast.error(`${describe(job.job_type)} failed`, {
          description: job.error_message || undefined,
        });
      }
    }
  }, [jobCenter, jobCenter?.jobs]);
}
